import type { RouterIds } from '@/Router';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { z } from 'zod';
import { type FilterIds, useFilterBarCtx } from './FilterBar';
import { __useLocalFilterStore } from './store';
import { cleanEmptyParams } from './utils';

const DEBOUNCE_TIME = 750;

/**
 * Clear filter state for internal filterbar components
 */
export const __useClearFilterState = () => {
  const filterCtx = useFilterBarCtx();
  const [localState, setLocalState] = __useLocalFilterStore(filterCtx.id as FilterIds['routeId']);

  const navigate = useNavigate();

  const hasFilters = useMemo(() => {
    if (!localState) {
      return false;
    }

    return Object.keys(localState).length > 0;
  }, [filterCtx, localState]);

  const resetAllFilters = useCallback(() => {
    if (filterCtx?.mode === 'routeId') {
      navigate({
        // @ts-ignore
        search: {},
        replace: true,
      });
    }

    setLocalState({});
  }, [filterCtx, navigate, setLocalState]);

  return { hasFilters, resetAllFilters };
};

/**
 * Manage filter state for internal filterbar components
 */
export function __useFilterState<Schema extends z.ZodType<unknown, z.ZodTypeDef>>(schema: Schema) {
  const filterCtx = useFilterBarCtx();
  const [localState, setLocalState] = __useLocalFilterStore<Schema>(filterCtx.id as FilterIds['routeId']);
  const [isUpdating, setIsUpdating] = useState(false);

  const navigate = useNavigate();

  const isUpdatingRef = useRef(false);
  const dataRef = useRef<Partial<z.infer<Schema>> | null>(null);

  const filterMode = useMemo(() => {
    return filterCtx?.mode;
  }, [filterCtx?.mode]);

  const routeApi = useMemo(() => {
    const mockRouteApi = {
      useSearch: () => ({}),
    };

    if (filterMode === 'storageKey') {
      return mockRouteApi;
    }

    try {
      return filterCtx.id ? getRouteApi(filterCtx.id as RouterIds) : mockRouteApi;
    } catch (e) {
      return mockRouteApi;
    }
  }, [filterCtx.id, filterMode]);

  const searchParams = routeApi.useSearch();

  const [state, setState] = useState<Partial<z.infer<Schema>>>(filterMode === 'routeId' ? searchParams : localState);
  const [slowFilters, setSlowFilters] = useState<Partial<z.infer<Schema>>>(localState);

  useEffect(() => {
    if (filterMode === 'storageKey') {
      const data = cleanEmptyParams((schema.safeParse(localState)?.data as Partial<z.infer<Schema>>) || {});
      setState(data);
      setSlowFilters(data);
      dataRef.current = data;
    }
  }, [localState, filterMode]);

  useEffect(() => {
    if (filterMode === 'routeId') {
      const data = cleanEmptyParams(schema.safeParse(searchParams)?.data) || {};
      setState(data);
      setSlowFilters(data);
      dataRef.current = data;
    }
  }, [searchParams, filterMode]);

  const updateFilter = useCallback(() => {
    setLocalState((prev) => {
      return cleanEmptyParams({
        ...prev,
        ...dataRef.current,
      });
    });

    if (filterMode === 'routeId') {
      navigate({
        // @ts-ignore
        search: (prev) => {
          return cleanEmptyParams({
            ...prev,
            ...dataRef.current,
          });
        },
        replace: true,
      });
    }

    setIsUpdating(false);
    isUpdatingRef.current = false;
  }, []);

  const updateDebounced = useCallback(debounce(updateFilter, DEBOUNCE_TIME, { leading: false, trailing: true }), []);

  type UpdateFilterFn = (prev: Partial<z.infer<Schema>>) => Partial<z.infer<Schema>>;
  type UpdateFilter = (cb: UpdateFilterFn | Partial<z.infer<Schema>>) => void;

  const updateFilterWrapper: UpdateFilter = useCallback(
    (cb) => {
      const data = cb instanceof Function ? cb(dataRef.current || {}) : cb;
      dataRef.current = data;
      setState(data);

      if (isUpdatingRef.current === false) {
        setIsUpdating(true);
        isUpdatingRef.current = true;
      }

      updateDebounced();
    },
    [updateDebounced]
  );

  return {
    fastFilters: state,
    slowFilters: slowFilters,
    updateFilter: updateFilterWrapper,
    isUpdating: isUpdating,
  };
}

/**
 * Subscribe to filter state for external components
 */
export function useFilterState<Schema extends z.ZodType<unknown, z.ZodTypeDef>>(
  schema: Schema,
  routeId: FilterIds['routeId']
) {
  const [localState] = __useLocalFilterStore<Schema>(routeId);

  const hasTsrRouteId = useMemo(() => {
    if (routeId.startsWith('key:')) {
      return false;
    }

    try {
      return !!getRouteApi(routeId as unknown as RouterIds);
    } catch (e) {
      return false;
    }
  }, [routeId]);

  const routeApi = useMemo(() => {
    const mockRouteApi = {
      useSearch: () => ({}),
    };

    if (routeId.startsWith('key:')) {
      return mockRouteApi;
    }

    try {
      return routeId ? getRouteApi(routeId as unknown as RouterIds) : mockRouteApi;
    } catch (e) {
      return mockRouteApi;
    }
  }, [routeId]);

  const searchParams = routeApi.useSearch();

  const [filters, setFilters] = useState<Partial<z.infer<Schema>>>(localState);
  useEffect(() => {
    if (hasTsrRouteId) {
      const data = schema.safeParse(searchParams)?.data || {};
      setFilters(data);
    }
  }, [searchParams, hasTsrRouteId]);

  useEffect(() => {
    if (!hasTsrRouteId) {
      const data = schema.safeParse(localState)?.data || {};
      setFilters(data);
    }
  }, [localState, hasTsrRouteId]);

  return filters;
}
